import JSZip from 'jszip';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  List,
  ListItem,
  Checkbox,
  useTheme,
  IconButton,
  Typography,
  ListItemText,
  ListItemAvatar,
} from '@mui/material';

import { useToast } from 'src/hooks/useToast';

import fileDownload from 'src/utils/fileDownload';

import { getFileByName } from 'src/services/file.service';
import { getTicketById } from 'src/services/ticket.service';

import Iconify from 'src/components/iconify';

import {
  listItemStyle,
  typographyStyle,
  attachmentListStyle,
  attachmentsHeaderStyle,
  attachmentsContainerStyle,
  attachmentsloadingButtonStyle,
} from '../styles';

export default function AttachmentsList({ ticketFilesList = [], ticketId = '' }) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);

  // Real-time state
  const [filesList, setFilesList] = useState(ticketFilesList);

  const getIconPath = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    const map = {
      pdf: 'pdf',
      xlsx: 'excel',
      xls: 'excel',
      csv: 'excel',
      docx: 'word',
      doc: 'word',
      pptx: 'power_point',
      ppt: 'power_point',
      zip: 'zip',
      rar: 'zip',
      '7z': 'zip',
      gz: 'zip',
      txt: 'text',
      json: 'text',
      jpg: 'image',
      jpeg: 'image',
      png: 'image',
      gif: 'image',
      webp: 'image',
      svg: 'image',
    };
    return `/assets/icons/Media/${map[ext] || 'text'}.svg`;
  };

  // 1. Sync local state when parent prop changes
  useEffect(() => {
    setFilesList(ticketFilesList);
  }, [ticketFilesList]);

  useEffect(() => {
    if (!ticketId) {
      return () => {};
    }

    const handleSilentRefresh = async (event) => {
      const updatedId = event.detail?.ticketId;
      if (updatedId && String(updatedId) === String(ticketId)) {
        try {
          const freshTicket = await getTicketById(ticketId);
          if (freshTicket && freshTicket.attachments) {
            setFilesList(freshTicket.attachments);
          }
        } catch (e) {
          console.error('Silently refreshing attachments failed:', e);
        }
      }
    };

    window.addEventListener('attachments-updated', handleSilentRefresh);
    return () => {
      window.removeEventListener('attachments-updated', handleSilentRefresh);
    };
  }, [ticketId]);

  // Updated to use filesList instead of ticketFilesList
  const allSelected = useMemo(
    () => filesList.length > 0 && selectedFiles.length === filesList.length,
    [selectedFiles.length, filesList.length]
  );
  const hasPartialSelection = useMemo(
    () => selectedFiles.length > 0 && selectedFiles.length < filesList.length,
    [selectedFiles.length, filesList.length]
  );

  const handleCheckboxChange = (fileName) => {
    setSelectedFiles((prev) =>
      prev.includes(fileName) ? prev.filter((f) => f !== fileName) : [...prev, fileName]
    );
  };

  // Updated to use filesList instead of ticketFilesList
  const handleSelectAll = () => setSelectedFiles(allSelected ? [] : [...filesList]);

  // Updated to use filesList instead of ticketFilesList
  const handleDownloadFiles = useCallback(
    async (files) => {
      const filesToDownload = files.length > 0 ? files : filesList;
      if (filesToDownload.length === 0) return;

      try {
        setIsDownloading(true);
        if (filesToDownload.length === 1) {
          const rawPath = filesToDownload[0];
          const fileName = rawPath.split(/[\\/]/).pop();
          const fileData = await getFileByName(fileName, ticketId);
          await fileDownload(fileData, fileName);
        } else {
          const zip = new JSZip();
          await Promise.all(
            filesToDownload.map(async (rawPath) => {
              const fileName = rawPath.split(/[\\/]/).pop();
              const fileData = await getFileByName(fileName, ticketId);
              zip.file(fileName, fileData);
            })
          );
          const zipBlob = await zip.generateAsync({ type: 'blob' });
          fileDownload(zipBlob, `ticket_${ticketId}_attachments.zip`);
        }
        showToast({
          message: t('pages.ticketDetails.downloadStarted'),
          severity: 'success',
        });
      } catch (error) {
        showToast({ message: t('errorMessages.couldntDownloadFile'), severity: 'error' });
      } finally {
        setIsDownloading(false);
        setSelectedFiles([]);
      }
    },
    [filesList, ticketId, t, showToast]
  );

  // Updated to use filesList instead of ticketFilesList
  if (!filesList.length) {
    return (
      <Box display="flex" height="150px" alignItems="center" justifyContent="center">
        <Typography variant="subtitle1" color="text.secondary">
          {t('pages.ticketDetails.noAttachments')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={attachmentsContainerStyle}>
      <Box sx={{ ...attachmentsHeaderStyle, bgcolor: theme.palette.action.hover }}>
        <Checkbox
          checked={allSelected}
          indeterminate={hasPartialSelection}
          onChange={handleSelectAll}
        />
        <Typography sx={typographyStyle}>{t('pages.formPage.selectAll')}</Typography>
      </Box>

      <List
        sx={{
          ...attachmentListStyle,
          maxHeight: '350px',
          backgroundColor: 'transparent',
        }}
      >
        {/* Updated map loop to iterate over filesList state */}
        {filesList.map((fileName, index) => {
          // --- LOGIC: SANITIZE DISPLAY NAME ---
          const cleanDisplayName = fileName
            .split(/[\\/]/)
            .pop()
            .replace(/_\d{2}_\d{2}_\d{4}.*(?=\.[^.]+$)/, '');

          return (
            <ListItem
              key={index}
              divider
              sx={{
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                transition: 'background-color 0.2s ease',
              }}
              secondaryAction={
                <IconButton onClick={() => handleDownloadFiles([fileName])}>
                  <Iconify
                    icon="solar:download-bold"
                    width={22}
                    color={theme.palette.primary.main}
                  />
                </IconButton>
              }
            >
              <Checkbox
                checked={selectedFiles.includes(fileName)}
                onChange={() => handleCheckboxChange(fileName)}
              />
              <ListItemAvatar>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                    borderRadius: '8px',
                  }}
                >
                  {/* Integrated Custom SVG Icon */}
                  <Box
                    component="img"
                    src={getIconPath(fileName)}
                    sx={{ width: 28, height: 28 }}
                  />
                </Box>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ ...listItemStyle, fontWeight: 700 }}
                  >
                    {cleanDisplayName}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>

      <LoadingButton
        fullWidth
        onClick={() => handleDownloadFiles(selectedFiles)}
        loading={isDownloading}
        disabled={selectedFiles.length === 0}
        variant="contained"
        startIcon={<Iconify icon="solar:download-minimalistic-bold" />}
        sx={{ ...attachmentsloadingButtonStyle, mt: 3 }}
      >
        {t('buttons.downloadSlectedFiles')} ({selectedFiles.length})
      </LoadingButton>
    </Box>
  );
}

AttachmentsList.propTypes = {
  ticketFilesList: PropTypes.array,
  ticketId: PropTypes.any,
};
